import { FC } from "react";
import { Navigate, useParams } from "react-router";

import TopNav from "../../components/TopNav/TopNav";
import OverviewHeader from "../../components/OverviewHeader/OverviewHeader";
import Section from "../../components/Section/Section";
import OverviewSourceUsageChart from "../../components/OverviewSourceUsage/OverviewSourceUsageChart/OverviewSourceUsageChart";
import SearchExportToolbar from "../../components/SearchExportToolbar";
import { Pagination } from "../../components/SourceTable/components/Pagination";
import SourceDetailChartSkeleton from "./components/SourceDetailChartSkeleton";
import SourceDetailLinksTable from "./components/SourceDetailLinksTable";
import SourceDetailLinksTableSkeleton from "./components/SourceDetailLinksTableSkeleton";
import { SourceUsageIcon, LinkIcon } from "../../components/icons";
import { ERoutes } from "../../constants/routes";
import { useSourceDetailPage } from "./hooks/useSourceDetailPage";

import styles from "./SourceDetail.module.css";

const SourceDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    domainRow,
    overviewReady,
    dateFilter,
    setDateFilterFromUrl,
    lastUpdateTime,
    chart,
    showChartSkeleton,
    chartEmpty,
    sourceDetailLinks,
    links,
    filteredLinks,
    linksPageRows,
    linksTotal,
    linksPageSize,
    linksCurrentPage,
    onLinksPageChange,
    searchInput,
    setSearchInput,
    handleExportToExcel,
  } = useSourceDetailPage();

  if (!id) {
    return <Navigate to={ERoutes.Source} replace />;
  }

  if (overviewReady && !domainRow) {
    return <Navigate to={ERoutes.Source} replace />;
  }

  const showLinksSkeleton =
    sourceDetailLinks.loading === "loading" || sourceDetailLinks.loading === "idle";

  return (
    <>
      <TopNav
        title={domainRow?.name ?? id}
        linkBack={ERoutes.Source}
      />
      <div className={styles["source_detail"]}>
        <div className={styles["source_detail__grid"]}>
          <div className={styles["source_detail__row"]}>
            <OverviewHeader
              lastUpdateTime={lastUpdateTime}
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilterFromUrl}
            />
          </div>

          <div className={styles["source_detail__row"]}>
            <Section icon={<SourceUsageIcon />} title="URLs Usage">
              {showChartSkeleton ? (
                <SourceDetailChartSkeleton />
              ) : chartEmpty ? (
                <p className={styles["source_detail__empty"]}>
                  No data for the selected period
                </p>
              ) : (
                <OverviewSourceUsageChart
                  data={chart.data}
                  series={chart.series}
                  hideMoreSourcesLegend
                  hideLegend
                />
              )}
            </Section>
          </div>

          <div className={styles["source_detail__row"]}>
            <Section
              icon={<LinkIcon />}
              title="URLs"
              headerRight={
                <SearchExportToolbar
                  searchValue={searchInput}
                  onSearchChange={(e) => setSearchInput(e.target.value)}
                  onExportClick={handleExportToExcel}
                />
              }
            >
              {showLinksSkeleton ? (
                <SourceDetailLinksTableSkeleton />
              ) : sourceDetailLinks.error ? (
                <p className={styles["source_detail__empty"]}>
                  {sourceDetailLinks.error}
                </p>
              ) : links.length === 0 ? (
                <p className={styles["source_detail__empty"]}>
                  No links for the selected period
                </p>
              ) : filteredLinks.length === 0 ? (
                <p className={styles["source_detail__empty"]}>
                  No links match your search
                </p>
              ) : (
                <>
                  <SourceDetailLinksTable
                    links={linksPageRows}
                    indexOffset={(linksCurrentPage - 1) * linksPageSize}
                  />
                  {linksTotal > linksPageSize && (
                    <Pagination
                      currentPage={linksCurrentPage}
                      totalPages={Math.ceil(linksTotal / linksPageSize)}
                      onPageChange={onLinksPageChange}
                    />
                  )}
                </>
              )}
            </Section>
          </div>
        </div>
      </div>
    </>
  );
};

export default SourceDetail;
